import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatFieldName, renderValue } from '../renderHelpers';
import { extractFormToken } from '../../../../Services/adminApiService';
import EmailStatusBadge from './EmailStatusBadge';
import PdfDownloadButton from './PdfDownloadButton';
import ResendEmailButton from './ResendEmailButton';

/**
 * CollapsibleRow - A row that can expand/collapse nested object values
 */
const CollapsibleRow = ({ label, value }) => {
  const isCollapsible = typeof value === 'object' && value !== null;
  const [open, setOpen] = useState(!isCollapsible);

  return (
    <div className="form-detail-row">
      <div
        className="form-detail-label"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{formatFieldName(label)}:</span>
        {isCollapsible && (
          <button
            className="btn-secondary"
            style={{ padding: '2px 8px', fontSize: '12px' }}
            onClick={() => setOpen(!open)}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      {open && <div className="form-detail-value">{renderValue(value)}</div>}
    </div>
  );
};

CollapsibleRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

/**
 * FormDetailModal - Modal dialog for viewing form details
 *
 * Shows a detailed view of a form submission in a modal overlay
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Object} props.form - The form data to display
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.sanitizeForm - Function to clean form data for display
 */
const FormDetailModal = ({ isOpen, form, onClose, sanitizeForm }) => {
  const [emailStatus, setEmailStatus] = useState(null);

  if (!isOpen || !form) return null;

  const sanitizedForm = sanitizeForm ? sanitizeForm(form) : form;
  const currentEmailStatus = emailStatus || form.emailStatus;
  const tokenInfo = extractFormToken(form.pdfDownloadUrl);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>
              {form.formType?.charAt(0).toUpperCase() + form.formType?.slice(1)}{' '}
              Form Details
            </h2>
            <p style={{ color: '#718096', margin: '4px 0 0 0' }}>
              Form ID: #{form.id}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Email Status & PDF Actions Bar */}
        {(currentEmailStatus || form.pdfDownloadUrl) && (
          <div className="modal-actions-bar">
            {currentEmailStatus && (
              <EmailStatusBadge status={currentEmailStatus} />
            )}
            <div className="form-action-buttons">
              {form.pdfDownloadUrl && (
                <PdfDownloadButton
                  pdfDownloadUrl={form.pdfDownloadUrl}
                  formName={form.formType}
                />
              )}
              {currentEmailStatus === 'FAILED' && tokenInfo && (
                <ResendEmailButton
                  formType={tokenInfo.formType}
                  formToken={tokenInfo.formToken}
                  onStatusUpdate={setEmailStatus}
                />
              )}
            </div>
          </div>
        )}

        <div className="modal-body">
          <div className="form-details-grid">
            {Object.entries(sanitizedForm).map(([key, value], index) => (
              <CollapsibleRow key={index} label={key} value={value} />
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

FormDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  form: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    formType: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  sanitizeForm: PropTypes.func,
};

FormDetailModal.defaultProps = {
  form: null,
  sanitizeForm: null,
};

export { CollapsibleRow };
export default FormDetailModal;
